import type { HouseholdInput, PersonInput } from '@/types';

type NormalizedPerson = {
  gross_salary: number;
  salary_sacrifice_contributions: number;
  taxable_benefits?: number;
  net_pension_contribution: number; // Normalized to single active value
  net_pension_contribution_type?: string; // Include type to detect percentage<->amount switches
  pension_type?: string;
  employer_pension_contribution: number; // Normalized to single active value
  employer_pension_contribution_type?: string; // Include type to detect percentage<->amount switches
  payroll_giving_donations: number;
  gift_aid_donations: number;
  charity_type?: string;
  tax_code: string;
};

type Normalized = {
  primary: NormalizedPerson;
  partner?: NormalizedPerson;
  children: Array<{ age: number }>;
  claims_child_benefit: boolean;
  preferred_strategy: HouseholdInput['preferred_strategy'] | null;
};

/**
 * Normalizes a PersonInput by selecting only the active pension contribution values.
 *
 * Since PersonInput stores both percentage and amount for pension contributions,
 * but only one is active at a time (determined by the _type field), this function
 * extracts only the active value to enable accurate equality comparison.
 *
 * This prevents false positives where the inactive value might differ between
 * two households even though the active value is the same.
 *
 * @param person - The person input to normalize
 * @returns A normalized person object with only active contribution values
 */
function normalizePerson(person: PersonInput): NormalizedPerson {
  // Get active pension contribution value based on type
  // If type is undefined or null, default to treating as 'amount' type
  const netPensionContribution =
    person.net_pension_contributions_type === 'percentage'
      ? person.net_pension_contributions_percentage ?? 0
      : person.net_pension_contributions_type === 'amount'
      ? person.net_pension_contributions_amount ?? 0
      : 0;

  // Get active employer pension contribution value based on type
  // If type is undefined or null, default to treating as 'amount' type
  const employerPensionContribution =
    person.employer_pension_contributions_type === 'percentage'
      ? person.employer_pension_contributions_percentage ?? 0
      : person.employer_pension_contributions_type === 'amount'
      ? person.employer_pension_contributions_amount ?? 0
      : 0;

  return {
    gross_salary: person.gross_salary,
    salary_sacrifice_contributions: person.salary_sacrifice_contributions,
    taxable_benefits: person.taxable_benefits,
    net_pension_contribution: netPensionContribution,
    net_pension_contribution_type: person.net_pension_contributions_type,
    pension_type: person.pension_type,
    employer_pension_contribution: employerPensionContribution,
    employer_pension_contribution_type: person.employer_pension_contributions_type,
    payroll_giving_donations: person.payroll_giving_donations,
    gift_aid_donations: person.gift_aid_donations,
    charity_type: person.charity_type,
    tax_code: person.tax_code,
  };
}

function normalize(input: HouseholdInput): Normalized {
  const children = (input.children ?? []).map((c) => ({ age: Number(c.age) }));
  children.sort((a, b) => a.age - b.age);

  return {
    primary: normalizePerson(input.primary),
    partner: input.partner ? normalizePerson(input.partner) : undefined,
    children,
    claims_child_benefit: Boolean(input.claims_child_benefit ?? false),
    preferred_strategy: input.preferred_strategy ?? null,
  };
}

export function householdEquals(a: HouseholdInput, b: HouseholdInput): boolean {
  try {
    const na = normalize(a);
    const nb = normalize(b);
    return JSON.stringify(na) === JSON.stringify(nb);
  } catch {
    return false;
  }
}
